import { useEffect, useRef, useState } from "react";
import { Download, Loader2, Trophy, X } from "lucide-react";
import { toPng } from "html-to-image";
import { CardView } from "./CardView";
import { fmtChips } from "../../lib/api";

const ordinal = (n) => (n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`);

// The winner's photograph: the board, the hand that won it and the chips, the way a live final table is shot.
export const MttFinish = ({ t, hand, seats = [], myUserId, onLobby }) => {
  const [shot, setShot] = useState(null);
  const [open, setOpen] = useState(false);
  const taken = useRef("");
  const card = useRef(null);
  const [saving, setSaving] = useState(false);
  // the champion's photo as a PNG for their phone / computer (buttons left out of the picture)
  const download = async () => {
    if (!card.current || saving) return;
    setSaving(true);
    try {
      const url = await toPng(card.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#0b0b0f",
        filter: (n) => !(n.dataset && n.dataset.noShot !== undefined) });
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(t.name || "tournament").replace(/[^a-z0-9]+/gi, "-")}-champion-${(shot?.champ?.name || "").replace(/[^a-z0-9]+/gi, "-")}.png`;
      a.click();
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (t?.status !== "FINISHED" || !t.payouts?.length) return;
    const key = `${t.instance_id}:${t.payouts.length}`;
    if (taken.current === key) return;
    taken.current = key;
    const champ = t.payouts.find((p) => p.place === 1);
    const seat = seats.find((s) => s.user_id === champ?.user_id);
    setShot({
      champ,
      board: hand?.board || [],
      hole: (seat?.hole || []).filter((c) => c && c !== "XX"),
    });
    setOpen(true);
  }, [t?.status, t?.payouts, t?.instance_id, hand?.board, seats]);

  if (!open || !shot) return null;
  const mine = shot.champ?.user_id === myUserId;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center p-6" data-testid="mtt-finish">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div ref={card} className="win-shot relative w-full max-w-3xl rounded-3xl overflow-hidden" data-testid="mtt-finish-card">
        <span className="win-rays" aria-hidden />
        {Array.from({ length: 28 }).map((_, i) => (
          <span key={i} className="win-confetti" aria-hidden
            style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i % 9) * 160}ms`, background: ["#f5c542", "#ffffff", "#49b0ff", "#ff5f8f", "#6ee7a8"][i % 5] }} />
        ))}

        <button data-no-shot onClick={() => setOpen(false)} data-testid="mtt-finish-close" aria-label="Close"
          className="absolute top-3 right-3 z-10 w-8 h-8 grid place-items-center rounded-full bg-black/40 hover:bg-black/70 text-white/80">
          <X className="w-4 h-4" />
        </button>

        <div className="relative px-8 pt-8 pb-7 flex flex-col items-center gap-5 text-center">
          <div className="flex flex-col items-center gap-1">
            <Trophy className="w-8 h-8 text-amber-400 win-trophy" />
            <p className="text-[10px] uppercase tracking-[0.34em] text-amber-200/80">{t.name} · Champion</p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none win-name" data-testid="mtt-finish-winner">
              {shot.champ?.name}
            </h2>
            <p className="text-sm text-amber-100/90">
              {mine ? "You took it down" : "Takes it down"}{shot.champ?.amount ? ` · ${fmtChips(shot.champ.amount)}` : ""}
            </p>
          </div>

          <div className="flex items-end justify-center gap-6 flex-wrap">
            {!!shot.board.length && (
              <div className="flex flex-col items-center gap-1.5">
                <span className="text-[9px] uppercase tracking-[0.26em] text-white/45">The board</span>
                <div className="flex gap-1.5" data-testid="mtt-finish-board">
                  {shot.board.map((c, i) => <CardView key={i} card={c} size="md" still />)}
                </div>
              </div>
            )}
            {!!shot.hole.length && (
              <div className="flex flex-col items-center gap-1.5">
                <span className="text-[9px] uppercase tracking-[0.26em] text-amber-200/70">Winning hand</span>
                <div className="flex gap-1.5" data-testid="mtt-finish-hole">
                  {shot.hole.map((c, i) => <CardView key={i} card={c} size="md" still highlight="win" />)}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-end justify-center h-48 w-full" data-testid="mtt-finish-chips">
            <img src="/assets/champion_pile-v2.png" alt="" aria-hidden="true"
              className="h-48 w-auto object-contain object-bottom win-chip drop-shadow-[0_14px_22px_rgba(0,0,0,0.7)]" />
          </div>

          <div className="w-full flex flex-col gap-1.5" data-testid="mtt-finish-places">
            {t.payouts.slice(0, 3).map((p) => (
              <div key={p.place} data-testid={`mtt-finish-place-${p.place}`}
                className={`flex items-center justify-between gap-3 rounded-full px-4 py-1.5 text-sm ${p.place === 1 ? "bg-amber-400/15 border border-amber-300/40" : "bg-white/5 border border-white/10"}`}>
                <span className="font-display tracking-wider text-xs text-amber-200/90">{ordinal(p.place)}</span>
                <span className="flex-1 text-left truncate">{p.name}{p.user_id === myUserId ? " (you)" : ""}</span>
                <span className="font-mono text-xs">{p.amount ? fmtChips(p.amount) : "—"}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap justify-center" data-no-shot>
            <button onClick={download} disabled={saving} data-testid="mtt-finish-download"
              className="rounded-full px-5 py-2 text-xs bg-amber-400/15 border border-amber-300/40 text-amber-100 hover:bg-amber-400/25 btn-lift flex items-center gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Save picture</button>
            <button onClick={onLobby} data-testid="mtt-finish-lobby"
              className="btn-gold rounded-full px-6 py-2 text-xs font-display tracking-wider btn-lift">BACK TO LOBBY</button>
            <button onClick={() => setOpen(false)} data-testid="mtt-finish-stay"
              className="rounded-full px-5 py-2 text-xs bg-white/5 hover:bg-white/10 btn-lift">Stay and look</button>
          </div>
        </div>
      </div>
    </div>
  );
};
